/**
 * Client-side AES-256-GCM memory encryption. The Node bundle backs
 * these calls with the Rust core via NAPI; the browser bundle swaps in
 * a Web Crypto implementation (see `src-ts/browser/memory-crypto.ts`)
 * via a tsup shim. The async surface is shared between both.
 */
import { native } from "../native.js";

/** Derive the 32-byte AES key from an agent's secp256k1 privkey (hex). Deterministic — same privkey always yields the same key. */
export async function deriveMemoryKey(privkey: string): Promise<Buffer> {
  return native.deriveMemoryKey(privkey);
}

/** Encrypt UTF-8 plaintext. `ciphertext` has the 16-byte GCM auth tag appended, matching the SDK's on-chain byte layout. */
export async function encryptMemoryContent(
  plaintext: string,
  key: Buffer,
): Promise<{ ciphertext: Buffer; nonce: Buffer }> {
  return native.encryptMemoryContent(plaintext, key);
}

/** Decrypt ciphertext produced by `encryptMemoryContent`. Throws on GCM tag mismatch — indicates tampered ciphertext or wrong key. */
export async function decryptMemoryContent(
  ciphertext: Buffer,
  nonce: Buffer,
  key: Buffer,
): Promise<string> {
  return native.decryptMemoryContent(ciphertext, nonce, key);
}
